import { useMemo } from "react";
import { sources, subSources } from "../constants/source-data";

interface UseSourceValidationProps {
  selectedSource: string;
  selectedSubSource: string;
  subSourceTexts: Record<string, string>;
  otherSourceText: string;
}

export const useSourceValidation = ({
  selectedSource,
  selectedSubSource,
  subSourceTexts,
  otherSourceText,
}: UseSourceValidationProps) => {
  const sourceId = useMemo(
    () => sources.find((s) => s.label === selectedSource)?.id,
    [selectedSource]
  );

  const isOtherSource = selectedSource === "기타";
  const hasSubSources = sourceId && subSources[sourceId];

  // 모든 하위 소스가 텍스트 입력인지 확인
  const allTextInputs = useMemo(() => {
    if (!hasSubSources) return false;
    return subSources[sourceId!].every(
      (subSource) => subSource.type === "text"
    );
  }, [hasSubSources, sourceId]);

  // 선택된 하위 소스가 텍스트 입력을 요구하는지 확인

  const selectedSubSourceHasTextInput = useMemo(() => {
    if (!hasSubSources || !selectedSubSource) return false;
    const currentSelectedSubSource = subSources[sourceId!].find(
      (s) => s.id === selectedSubSource
    );
    return currentSelectedSubSource?.subSubSources?.some(
      (subSubSource) => subSubSource.type === "text"
    );
  }, [hasSubSources, sourceId, selectedSubSource]);

  // 텍스트 입력이 필요한 하위 소스가 있는지 확인
  const hasTextInputs = useMemo(() => {
    if (!hasSubSources) return false;
    return subSources[sourceId!].some((subSource) => subSource.type === "text");
  }, [hasSubSources, sourceId]);

  // 기타 소스 검증
  const isOtherSourceValid = useMemo(() => {
    if (!isOtherSource) return true;
    return otherSourceText.trim().length > 0;
  }, [isOtherSource, otherSourceText]);

  // 하위 소스 선택 검증
  const isSubSourceValid = useMemo(() => {
    if (!hasSubSources) return true;
    if (allTextInputs) return true;
    return selectedSubSource.length > 0;
  }, [hasSubSources, allTextInputs, selectedSubSource]);

  // 텍스트 입력 검증
  const isTextInputValid = useMemo(() => {
    if (!hasSubSources) return true;

    if (allTextInputs) {
      // 모든 하위 소스가 텍스트 입력인 경우
      return subSources[sourceId!].some((subSource) =>
        subSourceTexts[subSource.id]?.trim()
      );
    }

    if (hasTextInputs || selectedSubSourceHasTextInput) {
      // 텍스트 입력이 필요한 경우
      return subSources[sourceId!].some((subSource) => {
        if (subSource.type === "text") {
          return subSourceTexts[subSource.id]?.trim();
        }
        if (subSource.type === "button" && subSource.subSubSources) {
          return subSource.subSubSources.some(
            (subSubSource) =>
              subSubSource.type === "text" &&
              subSourceTexts[subSubSource.id]?.trim()
          );
        }
        return false;
      });
    }

    return true;
  }, [
    hasSubSources,
    sourceId,
    allTextInputs,
    hasTextInputs,
    selectedSubSourceHasTextInput,
    subSourceTexts,
  ]);

  // 전체 검증
  const isValid = useMemo(() => {
    if (!selectedSource) return false;
    return isOtherSourceValid && isSubSourceValid && isTextInputValid;
  }, [selectedSource, isOtherSourceValid, isSubSourceValid, isTextInputValid]);

  // 버튼 텍스트 결정
  const buttonText = useMemo(() => {
    if (!selectedSource) return "완료";

    if (isOtherSource && !otherSourceText.trim()) {
      return "출처를 입력해주세요";
    }

    if (hasSubSources) {
      if (allTextInputs) {
        const hasRequiredText = subSources[sourceId!].some((subSource) =>
          subSourceTexts[subSource.id]?.trim()
        );
        return hasRequiredText ? "완료" : "정보를 입력해주세요";
      }

      if (!selectedSubSource) {
        return "선택해주세요";
      }

      if (hasTextInputs || selectedSubSourceHasTextInput) {
        const hasRequiredText = subSources[sourceId!].some((subSource) => {
          if (subSource.type === "text") {
            return subSourceTexts[subSource.id]?.trim();
          }
          if (subSource.type === "button" && subSource.subSubSources) {
            return subSource.subSubSources.some(
              (subSubSource) =>
                subSubSource.type === "text" &&
                subSourceTexts[subSubSource.id]?.trim()
            );
          }
          return false;
        });
        return hasRequiredText ? "완료" : "정보를 입력해주세요";
      }

      return "완료";
    }

    return "완료";
  }, [
    selectedSource,
    isOtherSource,
    otherSourceText,
    hasSubSources,
    sourceId,
    allTextInputs,
    selectedSubSource,
    hasTextInputs,
    selectedSubSourceHasTextInput,
    subSourceTexts,
  ]);

  return {
    sourceId,
    isOtherSource,
    hasSubSources,
    allTextInputs,
    selectedSubSourceHasTextInput,
    hasTextInputs,
    isOtherSourceValid,
    isSubSourceValid,
    isTextInputValid,
    isValid,
    buttonText,
  };
};
